import { useEffect, useState } from 'react';
import { useDecision } from '@optimizely/react-sdk';
import type { ChatbotVendor } from '@fff-web/chatbot';

export const GIGA_CHATBOT_FLAG = 'giga_chatbot_activation';
export const GIGA_DECISION_TIMEOUT = 3000;

/**
 * Resolves the chatbot vendor from `giga_chatbot_activation`, like maui: Giga when on, Applied Labs
 * when off or when the Optimizely client is not ready within the timeout. `null` until resolved so the
 * widget never mounts Applied first and swaps; the first resolved value is kept for the same reason.
 * Must be called inside an OptimizelyProvider.
 */
export const useChatbotVendor = (): ChatbotVendor | null => {
  const [decision, clientReady, didTimeout] = useDecision(GIGA_CHATBOT_FLAG, {
    timeout: GIGA_DECISION_TIMEOUT,
  });
  const [fallbackElapsed, setFallbackElapsed] = useState(false);
  const [vendor, setVendor] = useState<ChatbotVendor | null>(null);

  // useDecision never times out when the client fails to initialize, so keep a timeout of our own.
  useEffect(() => {
    if (clientReady || didTimeout) return undefined;
    const timeoutId = setTimeout(() => setFallbackElapsed(true), GIGA_DECISION_TIMEOUT);
    return () => clearTimeout(timeoutId);
  }, [clientReady, didTimeout]);

  useEffect(() => {
    if (vendor !== null) return;
    if (clientReady) {
      setVendor(decision.variationKey === 'on' ? 'giga' : 'applied');
      return;
    }
    if (didTimeout || fallbackElapsed) setVendor('applied');
  }, [clientReady, didTimeout, fallbackElapsed, decision, vendor]);

  return vendor;
};
